import fetch from 'isomorphic-fetch';

import * as apiConstants from './apiConstants';
import * as dataUtils from '../../utils/dataUtils';

import config from '../../config';

import ApiResponse from './Response/ApiResponse';
import PaginationApiRequest from './Request/PaginationApiRequest';
import PaginationApiResponse from './Response/PaginationApiResponse';

const ESCAPED_STRING = '********';
const SECURE_FIELDS = [
    apiConstants.HTTP_HEADER_AUTHORIZATION,
    'password',
    'passwordConfirmation'
];

const PARSER_BLOB = response => response.blob();
const PARSE_JSON = response => response.json();


const MIME_TYPE_TO_RESPONSE_BODY_PARSER = {
    [apiConstants.HTTP_HEADER_CONTENT_TYPE_APPLICATION_OCTET_STREAM]: PARSER_BLOB,
    [apiConstants.HTTP_HEADER_CONTENT_TYPE_JSON]: PARSE_JSON,
    [apiConstants.HTTP_HEADER_CONTENT_TYPE_JSON_UTF8]: PARSE_JSON,
    [apiConstants.HTTP_HEADER_CONTENT_TYPE_PROBLEM_JSON]: PARSE_JSON
};


class ApiService {


    /**
     * @param request {ApiRequest} Prepared API request
     * @return {Promise<ApiResponse>}
     */
    async execute(request) {

        // dispatch init as we want to know what requests have been issued
        this.#triggerDispatches(apiConstants.REQUEST_INIT, request);

        const url = request.getUrl();
        const fetchConfig = ApiService.#createFetchConfig(request);

        try {

            const fetchResponse = await fetch(url, fetchConfig);
            const response = await this.#createResponse(request, fetchResponse);

            this.#dispatchResponse(request, response);

            return response;

        } catch (error) {

            // hard execution failure
            this.#triggerDispatches(apiConstants.REQUEST_FAILURE, request, null, error);
            this.#throwError(error, request);
        }
    }


    /**
     * @param request {ApiRequest} Api request to be executed
     * @param errorMessage {string|null} Optional error arguments
     * @return {Promise<*|undefined>}
     */
    async executeOrFail(request, errorMessage) {

        const response = await this.execute(request);
        if (response.isSuccessful()) {

            return response.getBody();
        }

        this.#throwError(new Error(errorMessage || 'Error executing request'), request, response);
    }


    /**
     * @param request {ApiRequest}
     * @returns {{headers: {}, method: *}}
     * @private
     */
    static #createFetchConfig(request) {

        const requestConfig = {
            headers: request.getHeaders(),
            method: request.getMethod()
        };

        let body = request.getBody();
        if (body !== null) {

            // if needed, introduce multiple data-types here
            if (request.isJson()) {
                body = JSON.stringify(body);
            }
            requestConfig.body = body;
        }

        return requestConfig;
    }


    /**
     * @param request {ApiRequest} Response triggered request
     * @param fetchResponse {Response} WebAPI response
     * @private
     */
    async #createResponse(request, fetchResponse) {

        let response = new ApiResponse(fetchResponse.status);

        if (request instanceof PaginationApiRequest) {
            response = new PaginationApiResponse(response);
        }

        // extract headers
        fetchResponse.headers.forEach((value, name) => {
            response.setHeader(name, value);
        });

        const responseBody = await ApiService.#getParsedResponseBody(request, fetchResponse);
        response.setBody(responseBody);

        return response;
    }


    /**
     * @param request {ApiRequest} Response triggered request
     * @param fetchResponse {Response} WebAPI response
     * @return {mixed} Response body
     * @private
     */
    static async #getParsedResponseBody(request, fetchResponse) {

        const contentType = fetchResponse.headers.get(apiConstants.HTTP_HEADER_CONTENT_TYPE);

        // no response body -> return as is
        if (!contentType) {

            return fetchResponse.text();
        }

        /**
         * @type function(Response)
         */
        const parser = MIME_TYPE_TO_RESPONSE_BODY_PARSER[contentType];

        return await parser(fetchResponse);
    }


    /**
     * @param request {ApiRequest}
     * @param response {ApiResponse}
     * @private
     */
    #dispatchResponse(request, response) {

        // dispatch on status callback first
        this.#triggerDispatches(response.getStatusCode(), request, response);

        const dispatchType = response.isSuccessful()
            ? apiConstants.REQUEST_SUCCESS
            : apiConstants.REQUEST_FAILURE;

        this.#triggerDispatches(dispatchType, request, response);
    }


    /**
     * @param type {number|string} Dispatch type
     * @param request {ApiRequest} Issuing request
     * @param response {ApiResponse|null} Possible response
     * @param error {TypeError|Error|string|null} Possible error
     * @private
     */
    #triggerDispatches(type, request, response = null, error = null) {

        request.getDispatches(type)
            .forEach(callback => callback(request, response, error));
    }


    /**
     * Logs a console error and throws the error as well.
     * Takes all arguments for logging and checks any of them for an ApiResponse object.
     *
     * @param error {Error}
     * @param request {ApiRequest}
     * @param response {ApiResponse|undefined}
     */
    #throwError(error, request, response = undefined) {

        const errorLogParts = [
            error,
            request.toLog()
        ];
        if (response) {
            errorLogParts.push(response.toLog());
        }

        if (config.environment.isDevelopment) {
            console.error.apply(this, errorLogParts);
        }

        const errorMessage = errorLogParts.map(part => this.#toLogString(part))
            .join('\n\n');

        throw Error(errorMessage);
    }


    /**
     * @param data {object} Object to stringify
     */
    #toLogString(data) {

        if (dataUtils.isObject(data)) {

            const cleanedData = this.#removeSecureInformation(data);
            return JSON.stringify(cleanedData);
        }

        return '' + data;
    }


    /**
     * @param apiOperation {object} Api operation data
     * @return {object} Loggable output
     */
    #removeSecureInformation(apiOperation) {

        apiOperation.headers = this.#secureObject(apiOperation.headers);
        const body = apiOperation.body;
        apiOperation.body = dataUtils.isObject(body)
            ? this.#secureObject(body)
            : this.#secureString(body);

        return apiOperation;
    }


    /**
     * Escapse sensitives field values
     *
     * @param data {object} Object with potential sensitive field values
     * @returns {{}|*}
     */
    #secureObject(data) {

        return dataUtils.mapRecursive(data, (key, value) => {

            if (SECURE_FIELDS.includes(key)) {

                return ESCAPED_STRING;
            }

            return value;
        });
    }


    /**
     * @param input {String|undefined} Input to clear
     * @returns {String|*}
     */
    #secureString(input) {

        if (!input || !(input instanceof String)) {

            return input;
        }

        SECURE_FIELDS.forEach(secureField => {
            const regExp = new RegExp(`(${secureField})(=\s?|:\s?)([^&])`, 'g');
            input = input.replace(regExp, `$1$2${ESCAPED_STRING}`);
        });

        return input;
    }
}


export default ApiService;
